import { supabase } from "@/lib/supabase";
import type { Views } from "@synkro/db";

export type StockRow = Views<"inventory_status">;

export type StockFilter = "all" | "low" | "out";

export interface InventoryQuery {
  organizationId: string;
  filter: StockFilter;
  search?: string;
  page: number;
  pageSize: number;
}

/**
 * La vista `inventory_status` ya trae el umbral resuelto y el estado
 * calculado. Repetir ese `case` en el frontend seria una segunda fuente de
 * verdad que algun dia diverge de la primera.
 */
export async function fetchInventory({
  organizationId,
  filter,
  search,
  page,
  pageSize,
}: InventoryQuery): Promise<{ rows: StockRow[]; total: number }> {
  let query = supabase
    .from("inventory_status")
    .select("*", { count: "exact" })
    .eq("organization_id", organizationId)
    .order("quantity_available", { ascending: true })
    .range(page * pageSize, page * pageSize + pageSize - 1);

  if (filter === "low") query = query.eq("stock_status", "low");
  if (filter === "out") query = query.eq("stock_status", "out_of_stock");

  const term = search?.trim();
  if (term) {
    const escaped = term.replace(/[%,]/g, "");
    query = query.or(`product_name.ilike.%${escaped}%,sku.ilike.%${escaped}%`);
  }

  const { data, error, count } = await query;
  if (error) throw error;

  return { rows: (data ?? []) as StockRow[], total: count ?? 0 };
}

/**
 * Recibe la cantidad FINAL, no el delta: es lo que el operario ve en pantalla
 * y elimina los errores por doble clic. La funcion exige motivo y deja
 * movimiento en el libro mayor.
 */
export async function adjustInventory(input: {
  organizationId: string;
  warehouseId: string;
  variantId: string;
  newQuantity: number;
  reason: string;
  notes?: string;
}) {
  const { data, error } = await supabase.rpc("adjust_inventory", {
    p_organization_id: input.organizationId,
    p_warehouse_id: input.warehouseId,
    p_variant_id: input.variantId,
    p_new_quantity: input.newQuantity,
    p_reason: input.reason,
    p_notes: input.notes || undefined,
  });
  if (error) throw error;
  return data;
}

export async function fetchMovements(variantId: string) {
  const { data, error } = await supabase
    .from("inventory_movements")
    .select("id, movement_type, quantity, reserved_delta, resulting_available, reason, notes, created_at")
    .eq("variant_id", variantId)
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) throw error;
  return data ?? [];
}

export const ADJUST_REASONS = [
  "Recuento físico",
  "Merma",
  "Producto vencido",
  "Ingreso de compra",
  "Corrección de error",
];

export const MOVEMENT_LABEL: Record<string, string> = {
  initial: "Carga inicial",
  adjustment: "Ajuste",
  reservation: "Reserva por pedido",
  release: "Liberación",
  sale: "Venta entregada",
  return: "Devolución",
  cancellation: "Cancelación",
  transfer_in: "Traspaso de entrada",
  transfer_out: "Traspaso de salida",
  purchase: "Compra",
};
